import { useCallback, useRef, useState, type ReactNode } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

/**
 * Horizontal scroll row for touch kiosks, where dragging barely works: large
 * left/right buttons appear beside the row whenever its content overflows,
 * each one fading out once the row reaches that end. The horizontal sibling
 * of `ButtonScrollArea`.
 */
export function HorizontalButtonScroll({
  children,
  className,
}: {
  children: ReactNode;
  /** Classes for the scrolling container itself, e.g. snap settings. */
  className?: string;
}) {
  const { t } = useTranslation();
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [canScrollBack, setCanScrollBack] = useState(false);
  const [canScrollForward, setCanScrollForward] = useState(false);

  // Stable ref callback (React 19 runs its returned cleanup on unmount), so
  // the observers are attached once instead of on every render.
  const trackScroller = useCallback((scroller: HTMLDivElement) => {
    scrollerRef.current = scroller;
    const update = () => {
      setCanScrollBack(scroller.scrollLeft > 1);
      setCanScrollForward(
        scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 1,
      );
    };
    update();

    // Observing the content too catches items being added without the
    // container itself changing size.
    const observer = new ResizeObserver(update);
    observer.observe(scroller);
    if (scroller.firstElementChild) {
      observer.observe(scroller.firstElementChild);
    }
    scroller.addEventListener("scroll", update, { passive: true });
    return () => {
      observer.disconnect();
      scroller.removeEventListener("scroll", update);
      scrollerRef.current = null;
    };
  }, []);

  const scrollByPage = (direction: 1 | -1) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    scroller.scrollBy({
      left: direction * scroller.clientWidth * 0.8,
      behavior: "smooth",
    });
  };

  // Showing the buttons only narrows the row, so it cannot flip back to
  // fitting and make them flicker.
  const overflowing = canScrollBack || canScrollForward;

  return (
    <div className="flex items-stretch gap-3">
      {overflowing && (
        <ScrollButton
          label={t("Görgetés balra")}
          disabled={!canScrollBack}
          onClick={() => scrollByPage(-1)}
        >
          <ChevronLeft className="size-10" />
        </ScrollButton>
      )}
      <div
        ref={trackScroller}
        className={cn("min-w-0 flex-1 overflow-x-auto", className)}
      >
        {children}
      </div>
      {overflowing && (
        <ScrollButton
          label={t("Görgetés jobbra")}
          disabled={!canScrollForward}
          onClick={() => scrollByPage(1)}
        >
          <ChevronRight className="size-10" />
        </ScrollButton>
      )}
    </div>
  );
}

function ScrollButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className="flex w-16 shrink-0 items-center justify-center rounded-xl border-2 border-bub-vivid-blue/30 bg-white text-bub-vivid-blue transition-opacity active:bg-bub-vivid-blue/10 disabled:opacity-30"
    >
      {children}
    </button>
  );
}
